'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Checkbox, Badge, Heading, Text, Picture } from '../../components/index.js'
import { useSceneShelfContext } from './context.js'
import type { SceneProductCardSemanticName, SceneProductCardData } from './types.js'

export interface SceneProductCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 产品数据 */
  product: SceneProductCardData
  /** 是否选中 */
  selected?: boolean
  /** 选中状态变化回调 */
  onSelectChange?: (selected: boolean, product: SceneProductCardData) => void
  /** 是否显示标签 */
  showTags?: boolean
  /** 是否显示原价 */
  showOriginalPrice?: boolean
  /** 语义化类名 */
  classNames?: Partial<Record<SceneProductCardSemanticName, string>>
}

/**
 * SceneProductCard - 场景推荐货架产品卡片
 *
 * @description 用于场景推荐货架的可选中产品卡片组件
 */
const SceneProductCard = React.forwardRef<HTMLDivElement, SceneProductCardProps>(
  (
    {
      className,
      classNames: classNamesProp,
      product,
      selected: selectedProp,
      onSelectChange: onSelectChangeProp,
      showTags: showTagsProp,
      showOriginalPrice: showOriginalPriceProp,
      ...props
    },
    ref
  ) => {
    // 从 Context 读取配置
    const context = useSceneShelfContext()

    // Props 优先级高于 Context
    const showTags = showTagsProp ?? context?.showTags ?? true
    const showOriginalPrice = showOriginalPriceProp ?? context?.showOriginalPrice ?? true
    const classNames = classNamesProp ?? context?.classNames ?? {}

    // 选中状态和回调从 Context 获取
    const selected = selectedProp ?? (context ? context.selectedIds.has(product.id) : false)
    const onSelectChange = onSelectChangeProp ?? context?.onSelectChange
    const disabled = product.soldOut ?? false

    const handleCheckboxChange = (checked: boolean | 'indeterminate') => {
      if (disabled) return
      if (typeof checked === 'boolean') {
        onSelectChange?.(checked, product)
      }
    }

    const handleCardClick = () => {
      if (disabled) return
      onSelectChange?.(!selected, product)
    }

    return (
      <div
        ref={ref}
        className={cn(
          'lg-desktop:gap-2 lg-desktop:h-[362px] lg-desktop:px-6 lg-desktop:pb-6 lg-desktop:pt-4 rounded-box box-border flex h-[284px] flex-col justify-between gap-2 border-2 border-solid p-4 pb-6 transition-colors',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          selected
            ? cn('border-brand-0 bg-container-secondary-0', classNames?.cardSelected)
            : cn('border-lines bg-container-primary', classNames?.card),
          className
        )}
        onClick={handleCardClick}
        data-headless-type-name="SceneShelf#ProductCard"
        data-headless-title-desc-button={`${product.custom_name ?? product.title}##Select`}
        data-headless-sku={product.id}
        {...props}
      >
        {/* 标签区域 */}
        <div
          className={cn(
            'lg-desktop:left-6 lg-desktop:top-6 flex max-h-[24px] min-h-[24px] flex-wrap gap-1 overflow-visible pr-9',
            classNames?.tags
          )}
        >
          {showTags &&
            product.tags?.map((tag, index) => (
              <Badge key={index} variant={tag.variant || 'outline'} size="sm">
                {tag.label}
              </Badge>
            ))}
        </div>

        {/* Checkbox */}
        <Checkbox
          checked={selected}
          disabled={disabled}
          onCheckedChange={handleCheckboxChange}
          onClick={e => e.stopPropagation()}
          className={cn(
            'text-brand-0 border-brand-0 lg-desktop:right-6 lg-desktop:top-6 absolute right-4 top-4 size-5 shrink-0',
            classNames?.checkbox
          )}
        />

        {/* 产品图片 */}
        <div className={cn('flex flex-1 grow-0 items-center justify-center', classNames?.image)}>
          <Picture
            source={product.imageUrl}
            alt={product.imageAlt || product.title}
            className="lg-desktop:size-[175px] aspect-square size-[124px] "
            imgClassName="object-contain"
          />
        </div>

        {/* 产品信息 */}
        <div className="flex grow flex-col justify-start gap-4">
          {/* 产品名称 */}
          <Heading
            as="h3"
            size={2}
            className={cn('text-info-primary line-clamp-2 h-[2.2em]', classNames?.title)}
            html={product.custom_name ?? product.title}
          />

          {/* 价格区域 */}
          <div className={cn('flex flex-wrap items-center gap-1', classNames?.priceWrapper)}>
            <Text
              as="span"
              size={2}
              className={cn('text-info-primary lg-desktop:text-[24px] text-[20px]', classNames?.currentPrice)}
            >
              {product.currentPrice.formatted}
            </Text>
            {showOriginalPrice && product.originalPrice && product.originalPrice.value > product.currentPrice.value && (
              <Text
                as="span"
                size={2}
                className={cn(
                  'text-info-quaternary lg-desktop:text-[24px] text-[20px] line-through',
                  classNames?.originalPrice
                )}
              >
                {product.originalPrice.formatted}
              </Text>
            )}
          </div>
        </div>
      </div>
    )
  }
)

SceneProductCard.displayName = 'SceneShelf.ProductCard'

export { SceneProductCard }
